// react
import React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DataObjectRoundedIcon from '@mui/icons-material/DataObjectRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import DynamicFormRoundedIcon from '@mui/icons-material/DynamicFormRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ModelTrainingRoundedIcon from '@mui/icons-material/ModelTrainingRounded';
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded';

// material ui
import {
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Divider,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material';

export default function SubscriptionActionsCell({
  row,
  onView,
  onEdit,
  onDelete,
  onAlgorithmInputOpen,
  onAlgorithmInputFormOpen,
  onOccupancyPredictionAnalysisOpen,
  onResetMachineLearningModel,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorEl && anchorEl.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleMenuItemClick = (option) => {
    setOpen(false);
    if (option === 'view') {
      onView(row);
    }
    if (option === 'edit') {
      onEdit(row);
    }
    if (option === 'delete') {
      onDelete(row);
    }
    if (option === 'algorithmInput') {
      onAlgorithmInputOpen(row);
    }
    if (option === 'algorithmInputForm') {
      onAlgorithmInputFormOpen(row);
    }
    if (option === 'occupancyModel') {
      onOccupancyPredictionAnalysisOpen(row);
    }
    if (option === 'reset') {
      onResetMachineLearningModel(row);
    }
  };

  return (
    <>
      <ButtonGroup
        variant="outlined"
        size="small"
        ref={setAnchorEl}
        aria-label="split button"
        sx={{
          '& .MuiButtonGroup-grouped:not(:last-of-type)': {
            borderRight: 'none',
          },
        }}
      >
        <Button
          size="small"
          onClick={() => handleMenuItemClick('view')}
          sx={{
            width: 75,
            borderColor: 'divider',
            ':hover': { borderColor: 'divider' },
          }}
          //   startIcon={<EditRoundedIcon />}
        >
          {t('actions.view')}
        </Button>
        <Button
          size="small"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="menu"
          variant="outlined"
          onClick={handleToggle}
          sx={{
            borderColor: 'divider',
            ':hover': { borderColor: 'divider', borderRightColor: 'divider' },
          }}
        >
          <ArrowDropDownIcon fontSize="small" />
        </Button>
      </ButtonGroup>
      <Popper
        open={open}
        anchorEl={anchorEl}
        role={undefined}
        transition
        style={{ zIndex: 1301 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  id="split-button-menu"
                  autoFocusItem
                  dense
                  sx={{ p: 1 }}
                >
                  <Tooltip
                    title={t('buildingOptimization:tooltips.jsonEditor')}
                    placement="left"
                    arrow
                    enterDelay={500}
                  >
                    <MenuItem
                      onClick={() => handleMenuItemClick('algorithmInput')}
                      sx={{
                        py: '6px',
                        px: '12px',
                        borderRadius: '5px',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <DataObjectRoundedIcon sx={{ mr: 1 }} />
                        <Typography variant="body2" fontWeight="medium">
                          {t('glossary.algorithmInput', {
                            ns: 'buildingOptimization',
                          })}
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Tooltip>

                  <Tooltip
                    title={t('buildingOptimization:tooltips.manualForm')}
                    placement="left"
                    arrow
                    enterDelay={500}
                  >
                    <MenuItem
                      onClick={() => handleMenuItemClick('algorithmInputForm')}
                      sx={{
                        py: '6px',
                        px: '12px',
                        borderRadius: '5px',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <DynamicFormRoundedIcon sx={{ mr: 1 }} />
                        <Typography variant="body2" fontWeight="medium">
                          {t('glossary.algorithmInput', {
                            ns: 'buildingOptimization',
                          })}
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Tooltip>

                  <Tooltip
                    title={t(
                      'buildingOptimization:tooltips.viewOccupancyModel',
                    )}
                    placement="left"
                    arrow
                    enterDelay={500}
                  >
                    <MenuItem
                      onClick={() => handleMenuItemClick('occupancyModel')}
                      sx={{
                        py: '6px',
                        px: '12px',
                        borderRadius: '5px',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <ModelTrainingRoundedIcon sx={{ mr: 1 }} />
                        <Typography variant="body2" fontWeight="medium">
                          {t('glossary.occupancyModel', {
                            ns: 'buildingOptimization',
                          })}
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Tooltip>

                  <Tooltip
                    title={t('buildingOptimization:tooltips.resetMLModel')}
                    placement="left"
                    arrow
                    enterDelay={500}
                  >
                    <MenuItem
                      onClick={() => handleMenuItemClick('reset')}
                      sx={{
                        py: '6px',
                        px: '12px',
                        borderRadius: '5px',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <RestoreRoundedIcon sx={{ mr: 1 }} />
                        <Typography variant="body2" fontWeight="medium">
                          {t('actions.resetItem', { item: 'ML Model' })}
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Tooltip>

                  <Divider></Divider>
                  <Tooltip
                    title={t('buildingOptimization:tooltips.editSubscription')}
                    placement="left"
                    arrow
                    enterDelay={500}
                  >
                    <MenuItem
                      onClick={() => handleMenuItemClick('edit')}
                      sx={{
                        py: '6px',
                        px: '12px',
                        borderRadius: '5px',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <EditRoundedIcon sx={{ mr: 1 }} />
                        <Typography variant="body2" fontWeight="medium">
                          {t('actions.edit')}
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Tooltip>

                  <Tooltip
                    title={t(
                      'buildingOptimization:tooltips.deleteSubscription',
                    )}
                    placement="left"
                    arrow
                    enterDelay={500}
                  >
                    <MenuItem
                      onClick={() => handleMenuItemClick('delete')}
                      sx={{
                        py: '6px',
                        px: '12px',
                        borderRadius: '5px',
                        ':hover': {
                          color: 'error.main',
                        },
                        transition: 'color 0.3s ease',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <DeleteRoundedIcon sx={{ mr: 1 }} />
                        <Typography variant="body2" fontWeight="medium">
                          {t('actions.delete')}
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Tooltip>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
